import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { TeamList, TeamUser } from '../api/types';
import {
  ActionMenu,
  ActionMenuItem,
  Alert,
  Button,
  Card,
  Field,
  Input,
  Modal,
  PasswordInput,
  Spinner,
} from '../components/ui';
import { useConfirm } from '../components/confirm';
import { useSession } from '../lib/session';
import { useT } from '../i18n/locale';
import { ASSIGNABLE_ROLE_LABEL, ROLE_LABEL, UserRole } from '../api/enums';

/**
 * Team management for a tenant.
 *
 * The plans sell a seat allowance (Basic 5, Pro 20, Enterprise unlimited) that
 * the server has always enforced and the buyer has never been able to see or
 * spend — the endpoints existed, the screen did not. So the seat count sits at
 * the top next to the limit, rather than being discovered as a refusal when
 * someone tries to add one person too many.
 */
export default function TeamScreen() {
  const { user: me } = useSession();
  const confirm = useConfirm();
  const t = useT();

  const [data, setData] = useState<TeamList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<TeamUser | null>(null);
  const [resetting, setResetting] = useState<TeamUser | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<TeamList>('/users'));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('team.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // A success notice is a receipt, not a standing message — clear it after a
  // few seconds so it does not linger over the table. Errors stay until the
  // next action replaces or clears them.
  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  const atLimit = data !== null && data.maxUsers !== null && data.seatsUsed >= data.maxUsers;

  async function update(u: TeamUser, patch: Partial<{ role: string; isActive: boolean }>) {
    setBusyId(u.id);
    setError(null);
    setNotice(null);
    try {
      await api.patch(`/users/${u.id}`, patch);
      await load();
      setNotice(t('team.changeSaved', { name: u.name }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('team.saveChangeFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(u: TeamUser) {
    if (u.is_active) {
      const ok = await confirm({
        title: t('team.deactivateTitle', { name: u.name }),
        tone: 'danger',
        confirmLabel: t('team.deactivate'),
        body: t('team.deactivateBody'),
        details: [
          { label: t('team.seatUsedLabel'), value: t('team.seatDecreases') },
          { label: t('team.reactivableLabel'), value: t('team.reactivableValue') },
        ],
      });
      if (!ok) return;
    }
    await update(u, { isActive: !u.is_active });
  }

  return (
    <div className="mx-auto w-full max-w-5xl p-4">
      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {notice ? (
        <div className="mb-3">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}

      <Card className="mb-3 flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <h2 className="m-0 text-base font-bold text-slate-800">{t('team.heading')}</h2>
          <p className="m-0 mt-0.5 text-sm text-slate-500">
            {data === null
              ? '—'
              : data.maxUsers === null
                ? t('team.activeUnlimited', { count: data.activeCount })
                : t('team.seatsUsed', { used: data.seatsUsed, max: data.maxUsers })}
          </p>
        </div>
        <Button onClick={() => setInviting(true)} disabled={loading || atLimit}>
          {t('team.add')}
        </Button>
      </Card>

      {atLimit ? <Alert tone="warn">{t('team.atLimit')}</Alert> : null}

      <Card className="overflow-hidden">
        {loading ? (
          <Spinner label={t('team.loadingUsers')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead className="border-b border-slate-200 bg-slate-100/60 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                <tr>
                  <th className="px-3 py-2">{t('team.colName')}</th>
                  <th className="px-3 py-2">{t('team.colEmail')}</th>
                  <th className="w-40 px-3 py-2">{t('team.colRole')}</th>
                  <th className="px-3 py-2">{t('team.colLastLogin')}</th>
                  <th className="w-16 px-3 py-2 text-right">{t('team.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.users ?? []).map((u) => {
                  const isOwner = u.role === UserRole.Owner;
                  const isMe = u.id === me?.id;
                  return (
                    <tr
                      key={u.id}
                      className={`border-b border-slate-100 ${u.is_active ? '' : 'bg-slate-50 text-slate-400'}`}
                    >
                      <td className="px-3 py-2 text-sm font-bold text-slate-900">
                        {u.name}
                        {isMe ? (
                          <span className="ml-1 text-xs text-slate-400">{t('common.you')}</span>
                        ) : null}
                        {!u.is_active ? (
                          <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                            {t('common.inactive')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-600">{u.email}</td>
                      <td className="px-3 py-2">
                        {/*
                          The owner's role is deliberately not editable: a tenant
                          keeps exactly one, and the server refuses to change it
                          here. Showing a dropdown that always fails would be
                          worse than showing none.
                        */}
                        {isOwner ? (
                          <span className="text-sm font-semibold text-slate-700">
                            {t(ROLE_LABEL[u.role])}
                          </span>
                        ) : (
                          <select
                            value={u.role}
                            disabled={busyId === u.id || !u.is_active}
                            onChange={(e) => void update(u, { role: e.target.value })}
                            className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm outline-none focus:border-blue-500 disabled:bg-slate-100"
                          >
                            {Object.entries(ASSIGNABLE_ROLE_LABEL).map(([value, label]) => (
                              <option key={value} value={value}>
                                {t(label)}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-500">
                        {u.last_login_at
                          ? new Date(u.last_login_at).toLocaleString('id-ID')
                          : t('common.never')}
                      </td>
                      <td className="px-3 py-2">
                        {/*
                          The owner's row is untouchable here and so is your
                          own deactivation — the server refuses both, and
                          hiding the controls means nobody learns that from an
                          error. Everything else an admin may actually do is
                          offered.
                        */}
                        <div className="flex justify-end">
                          {isOwner ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <ActionMenu label={t('team.colActions')}>
                              <ActionMenuItem
                                disabled={busyId === u.id}
                                onClick={() => setEditing(u)}
                              >
                                {t('common.edit')}
                              </ActionMenuItem>
                              {isMe ? null : (
                                <>
                                  <ActionMenuItem
                                    disabled={busyId === u.id}
                                    onClick={() => setResetting(u)}
                                  >
                                    {t('team.resetPassword')}
                                  </ActionMenuItem>
                                  <ActionMenuItem
                                    tone={u.is_active ? 'danger' : 'default'}
                                    disabled={busyId === u.id}
                                    onClick={() => void toggleActive(u)}
                                  >
                                    {u.is_active ? t('team.deactivate') : t('team.activate')}
                                  </ActionMenuItem>
                                </>
                              )}
                            </ActionMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing ? (
        <EditUser
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null);
            setNotice(t('team.userSaved', { name }));
            void load();
          }}
        />
      ) : null}

      {resetting ? (
        <ResetUserPassword
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={(name) => {
            setResetting(null);
            setNotice(t('team.passwordReset', { name }));
          }}
        />
      ) : null}

      {inviting ? (
        <InviteUser
          onClose={() => setInviting(false)}
          onCreated={(name) => {
            setInviting(false);
            setNotice(t('team.userAdded', { name }));
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

function InviteUser({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (name: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>(UserRole.Staff);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/users', { name, email, password, role });
      onCreated(name);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('team.addFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t('team.add')}
      onClose={onClose}
      dismissable={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={busy || name.trim().length < 2 || !email.trim() || !password}
          >
            {busy ? t('team.adding') : t('common.add')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Field label={t('team.colName')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('team.colEmail')} hint={t('team.emailHintNew')}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        {/*
          An admin creating the account sets the first password and hands it
          over, which is why the server marks it verified on creation: the
          address was vouched for by someone already trusted.
        */}
        <Field label={t('team.initialPassword')} hint={t('team.initialPasswordHint')}>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label={t('team.colRole')}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
          >
            {Object.entries(ASSIGNABLE_ROLE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function EditUser({
  user,
  onClose,
  onSaved,
}: {
  user: TeamUser;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unchanged = name.trim() === user.name && email.trim() === user.email;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/users/${user.id}`, { name: name.trim(), email: email.trim() });
      onSaved(name.trim());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('common.saveFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t('team.editTitle')}
      onClose={onClose}
      dismissable={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={busy || unchanged || name.trim().length < 2 || !email.trim()}
          >
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Field label={t('team.colName')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('team.colEmail')} hint={t('team.emailHintEdit')}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ResetUserPassword({
  user,
  onClose,
  onDone,
}: {
  user: TeamUser;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/users/${user.id}/password`, { newPassword: password });
      onDone(user.name);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('team.resetFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t('team.resetTitle', { name: user.name })}
      onClose={onClose}
      dismissable={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={() => void submit()} disabled={busy || !password}>
            {busy ? t('common.saving') : t('team.resetSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}
        <Alert tone="warn">
          {t('team.resetWarnPre', { name: user.name })}
          <b>{t('nav.account')}</b>
          {t('team.resetWarnPost')}
        </Alert>
        <Field label={t('common.newPassword')}>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="new-password"
          />
        </Field>
      </div>
    </Modal>
  );
}
